import type { SDKContext, TriggerCleanup } from "./types";
import { showPopup, isPopupVisible } from "./ui";
import { isInCooltime, isIos16PlusSafari, throttle } from "./utils";

// ---------------------------------------------------------------------------
// Guard: shared pre-check before showing popup
// ---------------------------------------------------------------------------

function shouldSuppress(ctx: SDKContext): boolean {
  return (
    isPopupVisible() ||
    window.isPopupForceHidden === true ||
    isInCooltime(String(ctx.config.popup_config_group_id))
  );
}

// ---------------------------------------------------------------------------
// 1. Browser Back trigger (most important)
//
// Strategy:
//   - Inject a history entry via pushState
//   - Listen for popstate to detect "back" navigation
//   - On iOS 16+ Safari, defer history injection until first user click
//     (because iOS 16+ restricts pushState timing)
// ---------------------------------------------------------------------------

export function setupBrowserBackTrigger(ctx: SDKContext): TriggerCleanup {
  if (!ctx.config.should_show_by_browser_back) return () => {};

  const HISTORY_KEY = "lrHistoryIndex";
  let historyInjected = false;

  /**
   * Inject two history entries: index 0 (replace current), index 1 (push new).
   * The user "going back" pops index 1, landing on index 0 where we intercept.
   */
  function injectHistory(): void {
    if (historyInjected) return;
    try {
      window.history.replaceState(
        { [HISTORY_KEY]: 0 },
        "",
        location.href,
      );
      window.history.pushState(
        { [HISTORY_KEY]: 1 },
        "",
        location.href,
      );
      historyInjected = true;
      // Store the URL so we can verify the popstate is for our injected entry
      sessionStorage.setItem("lr_prev_url", location.href);
    } catch {
      // history manipulation can fail in some restricted contexts
    }
  }

  // Decide when to inject history
  const isIos16Safari = isIos16PlusSafari();

  if (isIos16Safari && window.history.length > 1) {
    // iOS 16+ Safari: defer history injection to first user click
    const onFirstClick = () => {
      document.removeEventListener("click", onFirstClick, true);
      // Small delay to avoid interfering with the click itself
      setTimeout(injectHistory, 100);
    };
    document.addEventListener("click", onFirstClick, true);
  } else {
    // All other browsers: inject immediately
    injectHistory();
  }

  // Listen for popstate
  const handlePopstate = (_event: PopStateEvent) => {
    if (shouldSuppress(ctx)) return;

    const prevUrl = sessionStorage.getItem("lr_prev_url");
    // Verify this popstate is triggered by our injected history entry
    if (prevUrl === location.href && historyInjected) {
      showPopup(ctx, "back");
      // Re-push so back button still works if user closes popup
      try {
        window.history.pushState(
          { [HISTORY_KEY]: 1 },
          "",
          location.href,
        );
      } catch {
        // ignore
      }
    }
  };

  window.addEventListener("popstate", handlePopstate);

  return () => {
    window.removeEventListener("popstate", handlePopstate);
  };
}

// ---------------------------------------------------------------------------
// 2. Inactive Time trigger
//
// Start a timer for `first_waiting_sec` seconds.
// Any user interaction (scroll, click, keydown, mousemove, touchstart)
// resets the timer.
// ---------------------------------------------------------------------------

export function setupInactiveTimeTrigger(ctx: SDKContext): TriggerCleanup {
  if (!ctx.config.should_show_by_time) return () => {};

  const delaySec = ctx.config.first_waiting_sec;
  if (delaySec <= 0) return () => {};

  let timer: ReturnType<typeof setTimeout> | null = null;

  const USER_EVENTS: string[] = [
    "scroll",
    "click",
    "keydown",
    "mousemove",
    "touchstart",
    "touchmove",
  ];

  function startTimer(): void {
    clearTimer();
    timer = setTimeout(() => {
      if (shouldSuppress(ctx)) return;
      showPopup(ctx, "interval");
      removeListeners();
    }, delaySec * 1000);
  }

  function clearTimer(): void {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  }

  function resetTimer(): void {
    if (shouldSuppress(ctx)) {
      clearTimer();
      removeListeners();
      return;
    }
    startTimer();
  }

  function removeListeners(): void {
    clearTimer();
    USER_EVENTS.forEach((evt) => {
      window.removeEventListener(evt, resetTimer, { capture: true } as EventListenerOptions);
    });
  }

  // Attach reset listeners
  USER_EVENTS.forEach((evt) => {
    window.addEventListener(evt, resetTimer, { capture: true, passive: true });
  });

  // Start initial timer
  startTimer();

  return removeListeners;
}

// ---------------------------------------------------------------------------
// 3. Reactivation trigger (tab visibility change)
//
// When the page was hidden (via pagehide) and then becomes visible again
// via visibilitychange, show the popup.
// chatboost skips the first visibility after pagehide to avoid false triggers.
// ---------------------------------------------------------------------------

export function setupReactivationTrigger(ctx: SDKContext): TriggerCleanup {
  if (!ctx.config.should_show_by_reactivation) return () => {};

  let wasHidden = false;

  const handlePageHide = () => {
    wasHidden = true;
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState === "hidden") return;
    if (shouldSuppress(ctx)) return;

    if (wasHidden) {
      // Skip the first visibility change after pagehide
      // (chatboost behavior — avoids double-trigger on iOS)
      wasHidden = false;
      return;
    }

    showPopup(ctx, "reactivation");
  };

  window.addEventListener("pagehide", handlePageHide);
  document.addEventListener("visibilitychange", handleVisibilityChange);

  return () => {
    window.removeEventListener("pagehide", handlePageHide);
    document.removeEventListener("visibilitychange", handleVisibilityChange);
  };
}

// ---------------------------------------------------------------------------
// 4. In-scroll trigger
//
// Show popup when the user scrolls past a threshold percentage.
// Uses a 100ms throttle to avoid excessive calculations.
// ---------------------------------------------------------------------------

export function setupInscrollTrigger(ctx: SDKContext): TriggerCleanup {
  if (!ctx.config.should_show_by_inscroll) return () => {};

  const targetRate = ctx.config.scroll_rate;
  if (targetRate == null || targetRate <= 0) return () => {};

  const onScroll = throttle(100, () => {
    const scrollableHeight =
      document.documentElement.scrollHeight - window.innerHeight;
    if (scrollableHeight <= 0) return;

    const scrollPercent = Math.round(
      (window.scrollY / scrollableHeight) * 100,
    );

    if (scrollPercent >= targetRate) {
      window.removeEventListener("scroll", onScroll);
      if (shouldSuppress(ctx)) return;

      requestAnimationFrame(() => {
        showPopup(ctx, "inscroll");
      });
    }
  });

  window.addEventListener("scroll", onScroll, { passive: true });

  return () => {
    window.removeEventListener("scroll", onScroll);
  };
}

// ---------------------------------------------------------------------------
// Master setup: activate all enabled triggers
// ---------------------------------------------------------------------------

/**
 * Set up all enabled triggers based on the popup configuration.
 * Returns a cleanup function that removes all event listeners.
 */
export function activateAllTriggers(ctx: SDKContext): TriggerCleanup {
  const cleanups: TriggerCleanup[] = [
    setupBrowserBackTrigger(ctx),
    setupInactiveTimeTrigger(ctx),
    setupReactivationTrigger(ctx),
    setupInscrollTrigger(ctx),
  ];

  return () => {
    cleanups.forEach((fn) => fn());
  };
}
